import { useCallback, useEffect, useRef, useState } from 'react';
import useSlingApi from '@sdk/api/useSlingApi';
import { PosIntegrations } from '@sdk/integrations/types';
import { IntegrationFromApi } from '@sdk/integrations/types/apiTypes';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';

import { useNpsSurveyContext } from '@web/components/npsScore/NpsSurveyProvider/NpsSurveyProvider';

import { setCountdownDate } from '../externalRate/utils';

import { sendNpsFeedback, shouldDisplayNpsScore } from './utils';
import { Props } from './NpsScore';

export const useNpsScore = ({
  user,
  subscriptionName,
  usersCount,
  locationsCount,
  saveClientFlags,
  forceDisplay,
  onClose,
}: Props) => {
  const feedbackSentRef = useRef(false);
  const wasDisplayedRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { npsSurveyFormValues } = useNpsSurveyContext();
  const api = useSlingApi();

  const submitSurvey = useCallback(async () => {
    const { data: integrations } = await api.integrations.getAvailableIntegrations();
    const toastIntegrationStatus = getToastIntegrationStatus(integrations, user);

    await sendNpsFeedback(
      npsSurveyFormValues,
      {
        user,
        subscriptionName,
        usersCount,
        saveClientFlags,
        locationsCount,
      },
      toastIntegrationStatus
    );
  }, [locationsCount, npsSurveyFormValues, saveClientFlags, subscriptionName, user, usersCount]);

  const close = (): void => {
    if (!feedbackSentRef.current) {
      submitSurvey();
    }

    onClose?.();
    setIsVisible(false);
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (forceDisplay || (shouldDisplayNpsScore(user) && !wasDisplayedRef.current)) {
      timeoutId = setTimeout(() => {
        setIsVisible(true);
        wasDisplayedRef.current = true;
      }, 60 * 1000); // show modal 1 minute after logging in
    }

    return (): void => timeoutId && clearTimeout(timeoutId);
  }, [user, forceDisplay]);

  useEffect(() => {
    if (!feedbackSentRef.current && submitted) {
      submitSurvey();
      feedbackSentRef.current = true;
    }
  }, [submitSurvey, submitted]);

  const onPositiveRate = (): void => {
    setCountdownDate(user.settings.clientFlags, saveClientFlags);
  };

  return {
    close,
    isVisible,
    onPositiveRate,
    setSubmitted,
  };
};

export const getToastIntegrationStatus = (
  integrations: IntegrationFromApi[],
  currentUser: AuthUserType
) => {
  const { isToastBilling, isToastSignup } = currentUser.org;
  const toastIntegration = integrations.find((i) => i.type === PosIntegrations.toast);

  if (
    !isToastBilling &&
    !isToastSignup &&
    !toastIntegration?.enabled &&
    !toastIntegration?.lastUpdated
  ) {
    return 'Never assigned';
  }

  return !toastIntegration?.enabled ? 'Disabled' : 'Enabled';
};
